import React, { useState, useEffect } from 'react';
import { Sparkles, Check, Plus, ChevronDown, ChevronUp, Zap, Shield, TrendingUp, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


const PRIORITY_STYLES = {
  core: { icon: Shield, label: 'Essential', color: 'text-primary', bg: 'bg-primary/15 border-primary/25' },
  growth: { icon: TrendingUp, label: 'Growth', color: 'text-secondary', bg: 'bg-secondary/15 border-secondary/25' },
  situational: { icon: Target, label: 'Situational', color: 'text-muted-foreground', bg: 'bg-white/5 border-white/10' },
};

function HealthRing({ score, size = 48 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 70 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={3} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={3}
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold" style={{ color }}>{score}%</span>
      </div>
    </div>
  );
}

function RecCard({ rec, owned = false, onAdd, delay = 0 }) {
  const [expanded, setExpanded] = useState(false);
  const ps = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.situational;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`border rounded-xl transition-colors ${owned ? 'bg-green-500/[0.03] border-green-500/15' : `${ps.bg} border`}`}
      data-testid={`quiver-rec-${rec.type.toLowerCase().replace(/\s/g, '-')}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-3 flex items-center gap-3"
        data-testid={`quiver-toggle-${rec.type.toLowerCase().replace(/\s/g, '-')}`}
      >
        {owned ? (
          <div className="w-7 h-7 rounded-full bg-green-500/15 flex items-center justify-center flex-shrink-0">
            <Check className="w-3.5 h-3.5 text-green-400" />
          </div>
        ) : (
          <div className={`w-7 h-7 rounded-full ${ps.bg} flex items-center justify-center flex-shrink-0`}>
            <ps.icon className={`w-3.5 h-3.5 ${ps.color}`} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`text-sm font-medium ${owned ? 'text-green-300/80' : 'text-foreground'}`}>{rec.type}</p>
            <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${owned ? 'bg-green-500/15 text-green-400' : `${ps.bg} ${ps.color}`}`}>
              {owned ? 'Owned' : ps.label}
            </span>
          </div>
          {owned && rec.owned_boards && (
            <p className="text-[9px] text-green-400/50 truncate">
              {rec.owned_boards.map(b => `${b.brand} ${b.name}`).join(', ')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-0.5">
            {rec.tags.map(tag => (
              <span key={tag} className="w-1.5 h-1.5 rounded-full bg-primary/40" title={tag} />
            ))}
          </div>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/40" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-0">
              <div className="border-t border-white/5 pt-2 space-y-1.5">
                {rec.reasons.map((r, i) => (
                  <p key={i} className="text-[10px] text-muted-foreground/70 leading-relaxed">
                    {r}
                  </p>
                ))}
                <div className="flex flex-wrap gap-1 mt-1">
                  {rec.tags.map(tag => (
                    <span key={tag} className="text-[8px] bg-white/[0.04] text-muted-foreground/50 px-1.5 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {!owned && onAdd && (
                <button
                  data-testid={`quiver-add-${rec.type.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={(e) => { e.stopPropagation(); onAdd(rec.type); }}
                  className="mt-2 w-full text-[10px] py-2 rounded-lg bg-primary/15 text-primary border border-primary/25 hover:bg-primary/25 transition-colors inline-flex items-center justify-center gap-1 font-medium"
                >
                  <Plus className="w-3 h-3" />
                  Add {rec.type} to Garage
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function QuiverBuilder({ onAddBoard }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/api/boards/quiver/recommendation`);
        if (res.ok) setData(await res.json());
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return null;
  if (!data) return null;

  const { quiver_health: health, owned, missing, surf_level } = data;
  const totalMissing = missing.length;
  const coreMissing = missing.filter(m => m.priority === 'core');

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden" data-testid="quiver-builder">
      {/* Header */}
      <button
        data-testid="quiver-builder-toggle"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02] transition-colors"
      >
        <Sparkles className="w-4 h-4 text-primary/70" />
        <div className="flex-1 text-left">
          <p className="text-xs font-medium">Quiver Builder</p>
          <p className="text-[9px] text-muted-foreground/50">
            {totalMissing === 0 ? 'Your quiver is dialed!' : `${totalMissing} board${totalMissing > 1 ? 's' : ''} recommended`}
          </p>
        </div>
        <HealthRing score={health.score} size={36} />
        {collapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground/30" /> : <ChevronUp className="w-4 h-4 text-muted-foreground/30" />}
      </button>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-foreground">{health.boards_owned}</p>
                  <p className="text-[8px] text-muted-foreground/40">in garage</p>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-foreground">{health.types_covered}</p>
                  <p className="text-[8px] text-muted-foreground/40">types owned</p>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                  <p className={`text-sm font-bold ${health.core_coverage >= 100 ? 'text-green-400' : health.core_coverage >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {health.core_coverage}%
                  </p>
                  <p className="text-[8px] text-muted-foreground/40">core covered</p>
                </div>
              </div>

              {/* Missing boards */}
              {missing.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Zap className="w-3 h-3 text-primary/50" />
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40">Recommended</p>
                  </div>
                  <div className="space-y-1.5">
                    {missing.map((rec, i) => (
                      <RecCard
                        key={rec.type}
                        rec={rec}
                        delay={i * 0.04}
                        onAdd={onAddBoard ? (type) => onAddBoard(type) : null}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Owned boards */}
              {owned.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Check className="w-3 h-3 text-green-400/50" />
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40">Covered</p>
                  </div>
                  <div className="space-y-1.5">
                    {owned.map((rec, i) => (
                      <RecCard key={rec.type} rec={rec} owned delay={i * 0.03} />
                    ))}
                  </div>
                </div>
              )}

              {totalMissing === 0 && (
                <div className="text-center py-4">
                  <p className="text-xs text-green-400 font-medium">Your quiver covers all the bases</p>
                  <p className="text-[10px] text-muted-foreground/50 mt-0.5">for a {surf_level} surfer with your preferences</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
